/**
 * Soulog · "Plans ↔ FAQ" bottom switcher on the subscription page
 *
 * The form comes from the deck in the Feishu mock (bottom ‹ › + dots + 01/02 counter), but on the web it is not
 * a full-screen slideshow: each view is still normal, freely scrollable page content, only one view is shown at a time.
 *
 * Claiming: data-subs-view="<label>" on the section root element (emitted by each section's
 * "view label" setting). **Adjacent** sections with the same label are merged into one view — the subscription page merges
 * the "Plans" and "Credit packs" pricing-plans sections into the first view. Sections without a label
 * (hero, bottom CTA) don't take part in switching and are always shown.
 *
 * Progressive enhancement: when JS doesn't run all sections are visible and the page degrades into one long page,
 * with no content lost and no SEO impact — so hiding happens only in JS; CSS does not preset display:none.
 */
(function () {
	"use strict";

	/* The comparison table and FAQ sections each include this script once (they also need to work alone on other pages),
	   and the browser executes the same external script twice if it appears twice — without this guard two switcher bars get mounted
	   and keyboard ← → jumps two views at once. */
	if (window.__soulogSubsDeckLoaded) return;
	window.__soulogSubsDeckLoaded = true;

	var SELECTOR = "[data-subs-view]";
	var reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

	function init() {
		var marked = Array.prototype.slice.call(document.querySelectorAll(SELECTOR));
		if (marked.length < 2) return;

		/* What actually needs to be shown/hidden for each marked section is its outer .shopify-section wrapper —
		   the wrapper is the direct child of #main-content; hiding only the inner element leaves an empty shell,
		   adding a phantom gap between adjacent sections. */
		var views = [];
		marked.forEach(function (el) {
			var label = el.getAttribute("data-subs-view");
			var wrapper = el.closest(".shopify-section") || el;
			var last = views[views.length - 1];
			if (last && last.label === label) {
				last.wrappers.push(wrapper);
			} else {
				views.push({ label: label, wrappers: [wrapper] });
			}
		});
		if (views.length < 2) return;

		var nav = buildNav(views);
		document.body.appendChild(nav.root);

		var index = 0;

		function show(next, focusTop) {
			index = Math.max(0, Math.min(views.length - 1, next));
			views.forEach(function (view, i) {
				var active = i === index;
				view.wrappers.forEach(function (wrapper, k) {
					wrapper.hidden = !active;
					/* The hidden attribute gets overridden by display rules in some themes, so add an inline fallback.
					   Writing both makes it semantically correct (assistive tech skips it) and guaranteed not to display. */
					wrapper.style.display = active ? "" : "none";
					/* The first section of the current view must add its own top spacing — the hero only emits padding-top, not
					   padding-bottom (the help center's "one-sided spacing" model), so which section follows the hero
					   changes with switching and can't be hard-coded as "the hero's adjacent sibling" as before. */
					wrapper.classList.toggle("subs-view-lead", active && k === 0);
				});
			});
			nav.update(index, views);
			if (focusTop) {
				var first = views[index].wrappers[0];
				var top = first.getBoundingClientRect().top + window.pageYOffset;
				/* Subtract the header height so the heading isn't covered by the sticky header after switching */
				var header = document.querySelector(".header, header");
				var offset = header ? header.getBoundingClientRect().height : 0;
				window.scrollTo({
					top: Math.max(0, top - offset - 8),
					behavior: reduceMotion.matches ? "auto" : "smooth",
				});
			}
		}

		nav.onSelect = function (i) {
			show(i, true);
		};

		document.addEventListener("keydown", function (e) {
			var tag = e.target && e.target.tagName;
			if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
			if (e.metaKey || e.ctrlKey || e.altKey) return;
			if (e.key === "ArrowRight") show(index + 1, true);
			else if (e.key === "ArrowLeft") show(index - 1, true);
		});

		/* If the URL carries an anchor inside some view on page load (e.g. #faq), land directly on that view;
		   otherwise coming from the support page via "Subscriptions & Credits → FAQ" would show the first view. */
		var hash = window.location.hash.replace("#", "");
		if (hash) {
			views.forEach(function (view, i) {
				var hit = view.wrappers.some(function (wrapper) {
					return wrapper.querySelector("#" + CSS.escape(hash)) || wrapper.id === hash;
				});
				if (hit) index = i;
			});
		}
		show(index, false);
		watchVisibility(views, nav.root);
	}

	/* The switcher bar only appears while "the current view is still in the viewport" — after scrolling past the bottom CTA / footer
	   it has nothing to act on, and floating there would only cover content. Use IntersectionObserver rather than
	   scroll events: the latter need manual throttling and drop frames during iOS momentum scrolling. */
	function watchVisibility(views, navRoot) {
		if (!("IntersectionObserver" in window)) return;
		var visible = new Set();
		var observer = new IntersectionObserver(
			function (entries) {
				entries.forEach(function (entry) {
					if (entry.isIntersecting) visible.add(entry.target);
					else visible.delete(entry.target);
				});
				navRoot.classList.toggle("is-hidden", visible.size === 0);
			},
			/* 120px margin at the bottom: collapse as soon as the last line of content scrolls out of the viewport,
			   rather than waiting until it has fully left and having the bar flash above the footer. */
			{ rootMargin: "0px 0px -120px 0px" },
		);
		views.forEach(function (view) {
			view.wrappers.forEach(function (wrapper) {
				observer.observe(wrapper);
			});
		});
	}

	function buildNav(views) {
		var root = document.createElement("div");
		root.className = "subs-deck-nav";
		root.setAttribute("role", "tablist");
		root.setAttribute("aria-label", "Subscription views");

		var prev = arrowButton("prev", "M15 18l-6-6 6-6");
		var next = arrowButton("next", "M9 18l6-6-6-6");

		var counter = document.createElement("span");
		counter.className = "subs-deck-nav__counter";

		var dots = document.createElement("div");
		dots.className = "subs-deck-nav__dots";

		var api = { root: root, onSelect: function () {} };

		var buttons = views.map(function (view, i) {
			var dot = document.createElement("button");
			dot.type = "button";
			dot.className = "subs-deck-nav__dot";
			dot.setAttribute("role", "tab");
			/* The dot is the only labeled entry point; its text goes in aria-label for screen readers,
			   while visually the adjacent __label shows the current view name. */
			dot.setAttribute("aria-label", view.label);
			dot.addEventListener("click", function () {
				api.onSelect(i);
			});
			dots.appendChild(dot);
			return dot;
		});

		var label = document.createElement("span");
		label.className = "subs-deck-nav__label";

		prev.addEventListener("click", function () {
			api.onSelect(currentIndex - 1);
		});
		next.addEventListener("click", function () {
			api.onSelect(currentIndex + 1);
		});

		root.appendChild(prev);
		root.appendChild(counter);
		root.appendChild(dots);
		root.appendChild(label);
		root.appendChild(next);

		var currentIndex = 0;
		api.update = function (i, list) {
			currentIndex = i;
			counter.textContent = pad(i + 1) + " / " + pad(list.length);
			label.textContent = list[i].label;
			buttons.forEach(function (dot, k) {
				var active = k === i;
				dot.classList.toggle("is-active", active);
				dot.setAttribute("aria-selected", active ? "true" : "false");
			});
			prev.disabled = i === 0;
			next.disabled = i === list.length - 1;
		};
		return api;
	}

	function arrowButton(kind, path) {
		var button = document.createElement("button");
		button.type = "button";
		button.className = "subs-deck-nav__arrow";
		button.setAttribute("aria-label", kind === "prev" ? "Previous view" : "Next view");
		button.innerHTML =
			'<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
			'stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
			'<path d="' +
			path +
			'"/></svg>';
		return button;
	}

	function pad(n) {
		return n < 10 ? "0" + n : String(n);
	}

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", init);
	} else {
		init();
	}
})();
